import { gql, useSuspenseQuery } from '@apollo/client';
import { useMemo } from 'react';

import { DEFAULT_DASHBOARD_VARIATION, DashboardVariation } from '../../../../types/organization';
import { ContainerDefinitionKey } from '../definitions';
import { HOSPICE_DEMO_DASHBOARD_CONFIG } from './demo';
import { ELEANOR_HEALTH_DASHBOARD_CONFIG } from './eleanor';
import { HOME_HEALTH_DASHBOARD_CONFIG } from './homeHealth';
import { NORTH_STAR_DASHBOARD_CONFIG, NORTH_STAR_UAT_DASHBOARD_CONFIG } from './northStar';
import { TELADOC_DASHBOARD_CONFIG } from './teladoc';
import { IDashboardVariationConfig } from './types';

// Fetch the dashboard variation for the current user's organization or return
// the default dashboard variation.
const useDashboardVariation = (): DashboardVariation => {
    const { data } = useSuspenseQuery<{
        currentOrganization: {
            id: string;
            dashboardVariation: DashboardVariation;
        } | null;
    }>(gql`
        query OrganizationDashboardVariation {
            currentOrganization {
                id
                dashboardVariation
            }
        }
    `);

    // Purposefully memoizing without dependencies to only evaluate once so that
    // dashboard variation does not change out from under us.
    const dashboardVariation = useMemo(() => {
        if (data.currentOrganization?.dashboardVariation) {
            return data.currentOrganization.dashboardVariation;
        }
        console.info(
            'No dashboard variation set for organization, using default',
            DEFAULT_DASHBOARD_VARIATION,
        );
        return DEFAULT_DASHBOARD_VARIATION;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return dashboardVariation;
};

const DASHBOARD_VARIATION_CONFIG: Record<DashboardVariation, IDashboardVariationConfig | null> = {
    [DashboardVariation.ELEANOR_HEALTH]: ELEANOR_HEALTH_DASHBOARD_CONFIG,
    [DashboardVariation.HOME_HEALTH_DEMO]: HOME_HEALTH_DASHBOARD_CONFIG,
    [DashboardVariation.HOSPICE_DEMO]: HOSPICE_DEMO_DASHBOARD_CONFIG,
    [DashboardVariation.JET_HEALTH]: null,
    [DashboardVariation.NORTH_STAR]: NORTH_STAR_DASHBOARD_CONFIG,
    [DashboardVariation.NORTH_STAR_UAT]: NORTH_STAR_UAT_DASHBOARD_CONFIG,
    [DashboardVariation.TELADOC]: TELADOC_DASHBOARD_CONFIG,
};

export const useDashboardVariationConfig = (): IDashboardVariationConfig & {
    variation: DashboardVariation;
} => {
    const dashboardVariation = useDashboardVariation();
    const config = DASHBOARD_VARIATION_CONFIG[dashboardVariation] ?? { containers: [] };
    return {
        ...config,
        variation: dashboardVariation,
    };
};

export const getDefaultEnabledContainerKeys = (
    variation: DashboardVariation,
): ContainerDefinitionKey[] => {
    return (
        DASHBOARD_VARIATION_CONFIG[variation]?.containers
            .filter((container) => container.defaultEnabled)
            .map((container) => container.key) ?? []
    );
};
